"use client";

import { motion } from "framer-motion";

/* ================= MOTION ================= */

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.12,
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1], // premium easing
    },
  }),
};

/* ================= DATA ================= */

const LOAD_SYSTEM = [
  {
    index: "01",
    title: "The Big Three",
    tone: "Foundation",
    description:
      "These define the limits of your expedition. Everything else adapts around them.",
    items: [
      "Backpack: 40–65L, matched to expedition length, weather & terrain",
      "Shelter: Tent, bivy, or tarp system for exposure management",
      "Sleep System: Temperature-rated bag, insulated pad, optional liner",
    ],
  },
  {
    index: "02",
    title: "Clothing Architecture",
    tone: "Thermal Control",
    description:
      "Your clothing system regulates heat, moisture, and survivability across altitude and weather shifts.",
    items: [
      "Base layers: Merino or synthetic moisture-wicking tops & bottoms",
      "Insulation: Fleece mid-layer and down/synthetic puffy",
      "Shell: Waterproof, windproof jacket and pants",
      "Trail wear: Quick-dry clothing, spare socks, underwear",
      "Accessories: Sun/warmth hat, gloves, camp shoes or booties",
    ],
  },
  {
    index: "03",
    title: "Safety & Navigation",
    tone: "Decision Systems",
    description:
      "These tools turn uncertainty into calculated movement  and mistakes into recoverable moments.",
    items: [
      "Navigation: Map, compass, GPS/watch, power banks",
      "Illumination: Headlamp with backup batteries",
      "Emergency: PLB or satellite communicator, whistle, fire starter, first aid kit",
    ],
  },
  {
    index: "04",
    title: "Kitchen & Hydration",
    tone: "Sustainment",
    description:
      "Energy, hydration, and warmth  mismanage these and the expedition ends early.",
    items: [
      "Water: Filter or purifier, bottles or reservoir, insulated bottle",
      "Cooking: Stove, fuel, pot, utensils, mug",
      "Food: Calorie-dense meals, snacks, bear canister where required",
    ],
  },
  {
    index: "05",
    title: "Tools & Personal",
    tone: "Redundancy & Repair",
    description:
      "Small items that quietly prevent mission failure.",
    items: [
      "Tools: Multi-tool or knife, repair kit (duct tape, cordage)",
      "Hygiene: Toothbrush, sanitizer, toilet kit, sunscreen, insect repellent",
      "Documents: ID, permits, cash",
    ],
  },
];

const PACKING_ZONES = [
  {
    zone: "Bottom Layer",
    role: "Stability",
    text: "Bulky, lightweight items  sleeping bag, camp clothes.",
  },
  {
    zone: "Core Zone",
    role: "Balance",
    text: "Heaviest gear packed close to the spine  food, water, stove.",
  },
  {
    zone: "Top Layer",
    role: "Speed",
    text: "Rapid-access items  rain shell, snacks, first aid.",
  },
  {
    zone: "External Pockets",
    role: "Immediate Access",
    text: "Map, headlamp, knife, sunscreen.",
  },
];

/* ================= COMPONENT ================= */

export default function ExpeditionLoadSystem() {
  return (
    <section className="bg-[#1f2c38] text-white py-36 px-6 md:px-12">
      <div className="max-w-7xl mx-auto space-y-40">

        {/* ================= INTRO ================= */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-4xl"
        >
          <motion.span
            variants={reveal}
            className="inline-block text-xs uppercase tracking-[0.4em] text-neutral-400 mb-6"
          >
            Expedition Load System
          </motion.span>

          <motion.h1
            variants={reveal}
            className="text-5xl md:text-6xl font-semibold leading-[1.05]"
          >
            Every item earns its place.
            <br />
            Every gram has intent.
          </motion.h1>

          <motion.p
            variants={reveal}
            className="mt-8 text-lg text-neutral-300 max-w-2xl"
          >
            Expedition packing is not about carrying more 
            it’s about carrying correctly.  
            Weight, access, and redundancy determine success long before the climb begins.
          </motion.p>
        </motion.div>

        {/* ================= LOAD SYSTEM ================= */}
        <div className="space-y-32">
          {LOAD_SYSTEM.map((block, i) => (
            <motion.div
              key={block.index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-120px" }}
              custom={i}
              variants={reveal}
              className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-10"
            >
              {/* INDEX */}
              <div className="flex items-center justify-center text-gray-100/30 text-9xl font-semibold">
                {block.index}
              </div>

              {/* CONTENT */}
              <div className="space-y-6">
                <div>
                  <span className="text-xs uppercase tracking-[0.3em] text-neutral-400">
                    {block.tone}
                  </span>
                  <h2 className="mt-2 text-3xl md:text-4xl font-semibold">
                    {block.title}
                  </h2>
                </div>

                <p className="text-neutral-300 max-w-2xl">
                  {block.description}
                </p>

                <ul className="grid gap-4 pt-4">
                  {block.items.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-neutral-300"
                    >
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-white/60" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ================= PACKING STRATEGY ================= */}
        {/* <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="space-y-12"
        >
          <motion.h2
            variants={reveal}
            className="text-4xl font-semibold"
          >
            Load Distribution Strategy
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {PACKING_ZONES.map((zone, i) => (
              <motion.div
                key={zone.zone}
                custom={i}
                variants={reveal}
                className="rounded-2xl bg-gray-100/10 border border-white/10 p-6 backdrop-blur"
              >
                <span className="text-xs uppercase tracking-widest text-neutral-400">
                  {zone.role}
                </span>
                <h3 className="mt-2 text-lg font-medium">
                  {zone.zone}
                </h3>
                <p className="mt-3 text-sm text-neutral-300">
                  {zone.text}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div> */}

      </div>
    </section>
  );
}
